import { useState } from "react";
import { motion } from "framer-motion";
import QRCode from "react-qr-code";
import { Check, Copy, HandHeart, Landmark, QrCode } from "lucide-react";
import { toast } from "sonner";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

// Dados oficiais fornecidos pela Chancelaria — não alterar.
const PIX_FAVORECIDO = "Diocese Anglicana Católica do Brasil";
const PIX_KEY = "60.342.013/0001-70";
const PIX_PAYLOAD = "00020126360014br.gov.bcb.pix0114603420130001705204000053039865802BR5925DIOCESE ANGLICANA CATOLIC6010ESMERALDAS6226052226hQS9KvaGf32YJzXOuNjG6304DA42";

const BANK = [
  { label: "Instituição", value: "403 – Cora SCFI" },
  { label: "Agência", value: "0001" },
  { label: "Conta", value: "5810701-6" },
  { label: "Favorecido", value: "Diocese Anglicana Católica do Brasil" },
  { label: "CNPJ", value: "60.342.013/0001-70" },
];

async function copyText(text, okMsg, setDone) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
  toast.success(okMsg);
  setDone(true);
  setTimeout(() => setDone(false), 2200);
}

function CopyButton({ text, okMsg, testid, children }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testid}
      onClick={() => copyText(text, okMsg, setDone)}
      className="overline-label inline-flex items-center justify-center gap-2 bg-[#C5A059] px-6 py-3 text-[#2E070D] transition-colors hover:bg-[#D4AF37]"
    >
      {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {children}
    </button>
  );
}

export default function Doacoes() {
  return (
    <section id="doacoes" data-testid="doacoes-section" className="bg-[#2E070D] px-4 py-20 text-[#FBF9F4] sm:px-8 lg:px-16 lg:py-32">
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
        <motion.p variants={fadeUp} className="overline-label text-[#C5A059]">Ofertas e Dízimos</motion.p>
        <motion.h2 variants={fadeUp} className="font-display mt-4 text-4xl font-bold uppercase leading-tight sm:text-5xl">
          Doações
        </motion.h2>
        <motion.div variants={fadeUp} className="gold-rule mt-6 w-40" />
        <motion.p variants={fadeUp} className="font-liturgy mt-6 max-w-2xl text-xl italic leading-relaxed text-[#FBF9F4]/70">
          Contribua com a missão da Diocese por Pix ou transferência bancária.
        </motion.p>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <motion.div variants={fadeUp} data-testid="doacoes-pix-card" className="border border-[#C5A059]/30 p-8">
            <div className="flex items-center gap-3">
              <HandHeart className="h-7 w-7 text-[#C5A059]" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold uppercase tracking-wide">Pix Institucional</h3>
            </div>
            <dl className="mt-6 space-y-3">
              <div>
                <dt className="text-xs uppercase tracking-widest text-[#FBF9F4]/50">Favorecido</dt>
                <dd className="mt-0.5 text-sm font-semibold text-[#FBF9F4]">{PIX_FAVORECIDO}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-widest text-[#FBF9F4]/50">Chave Pix — CNPJ</dt>
                <dd data-testid="doacoes-pix-key" className="mt-0.5 font-display text-2xl font-bold tracking-wide text-[#C5A059]">{PIX_KEY}</dd>
              </div>
            </dl>
            <div className="mt-6">
              <CopyButton text={PIX_KEY} okMsg="Chave Pix copiada!" testid="pix-copy-key-button">
                Copiar chave Pix
              </CopyButton>
            </div>
          </motion.div>

          <motion.div variants={fadeUp} data-testid="doacoes-qrcode-card" className="border border-[#C5A059]/30 p-8">
            <div className="flex items-center gap-3">
              <QrCode className="h-7 w-7 text-[#C5A059]" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-bold uppercase tracking-wide">QR Code Pix</h3>
            </div>
            <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row sm:items-start">
              <div className="shrink-0 border border-[#C5A059]/50 bg-[#FBF9F4] p-6">
                <QRCode
                  value={PIX_PAYLOAD}
                  size={220}
                  bgColor="#FBF9F4"
                  fgColor="#1C1917"
                  data-testid="pix-qrcode"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm leading-relaxed text-[#FBF9F4]/70">
                  Aponte a câmera do seu celular para o QR Code ao lado para doar via Pix,
                  ou copie o código Pix Copia e Cola abaixo.
                </p>
                <div className="mt-5">
                  <CopyButton text={PIX_PAYLOAD} okMsg="Código Pix copiado!" testid="pix-copy-payload-button">
                    Copiar Pix Copia e Cola
                  </CopyButton>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div variants={fadeUp} data-testid="doacoes-bank-card" className="mt-6 border border-[#C5A059]/30 p-8">
          <div className="flex items-center gap-3">
            <Landmark className="h-7 w-7 text-[#C5A059]" strokeWidth={1.5} />
            <h3 className="font-display text-xl font-bold uppercase tracking-wide">Transferência Bancária</h3>
          </div>
          <dl className="mt-6 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-5" data-testid="doacoes-bank-info">
            {BANK.map((item) => (
              <div key={item.label}>
                <dt className="text-xs uppercase tracking-widest text-[#FBF9F4]/50">{item.label}</dt>
                <dd className="mt-0.5 text-sm font-semibold text-[#FBF9F4]">{item.value}</dd>
              </div>
            ))}
          </dl>
        </motion.div>
      </motion.div>
    </section>
  );
}
